import React, { useMemo } from 'react';
import { buildDependencies } from 'di/buildDependencies';
import { ChannelResolver as DummyChannelResolver } from 'infrastructure/dummy/ChannelResolver';
import { ChannelResolver as ApiChannelResolver } from 'infrastructure/t4/ChannelResolver';
import { MediaDeviceRepository as DummyMediaDeviceRepository } from 'infrastructure/dummy/MediaDeviceRepository';
import { MediaDeviceRepository as ApiMediaDeviceRepository } from 'infrastructure/t4/MediaDeviceRepository';
import { VehicleRepository as DummyVehicleRepository } from 'infrastructure/dummy/VehicleRepository';
import { VehicleRepository as ApiVehicleRepository } from 'infrastructure/t4/VehicleRepository';
import { CachedVehicleRepository } from 'core/repositories/CachedVehicleRepository';
import { OIDCClient } from 'infrastructure/oidc/OIDCClient';
import { useWithOIDCClient } from 'services/oidc/useOIDCClient';
import { createConfiguredApiCalls } from 'di/configuredApiCalls';
import { ConfigParams } from 'config';
import { LocalStorageCameraLayoutRepository } from 'infrastructure/layout/LocalStorageCameraLayoutRepository';
import { ProjectRepository as ApiProjectRepository } from 'infrastructure/t4/ProjectRepository';
import { ProjectRepository as DummyProjectRepository } from 'infrastructure/dummy/ProjectRepository';
import { EnvironmentRepository as ApiEnvironmentRepository } from 'infrastructure/t4/EnvironmentRepository';
import { EnvironmentRepository as DummyEnvironmentRepository } from 'infrastructure/dummy/EnvironmentRepository';
import { useWithRevokeToken } from 'services/useRevokeToken';
import { AuthTokenRepositoryImpl } from 'infrastructure/t4/AuthTokenRepository';
import { AuthTokenRepository as DummyAuthTokenRepository } from 'infrastructure/dummy/AuthTokenRepository';
import { VehicleConfigurationRepository } from 'infrastructure/t4/VehicleConfigurationRepository';
import { VehicleTelemetryRepositoryFactory } from 'infrastructure/t4/VehicleTelemetryRepositoryFactory';
import { BasicFMSWebsocketAPIFactory } from 'infrastructure/t4/api/FMS/wsFactory';
import { DummyTelemetryRepositoryFactory } from 'infrastructure/dummy/TelemetryRepositoryFactory';

type DependenciesContainerProps = {
    oidcClient?: OIDCClient;
    config: ConfigParams;
    onMaintenance: () => void;
};

const DependenciesContainer = (props: React.PropsWithChildren<DependenciesContainerProps>) => {
    const config = props.config;

    const callbackUrl = ((config.auth || {}).oidc || {}).redirectUri
        || `${window.location.protocol}//${window.location.host}${config.deployment.rootPath}/oidc/auth/cb/`;

    const oidcClient = useMemo(() => config.auth
        ? new OIDCClient({
            client: {
                id: config.auth.oidc.clientId,
                secret: config.auth.oidc.clientSecret,
            },
            issuer: config.auth.oidc.issuer,
            endpoint: {
                baseUrl: '',
                path: {
                    auth: config.auth.oidc.authEndpoint,
                    token: config.auth.oidc.tokenEndpoint,
                    endSession: config.auth.oidc.endSessionEnpoint,
                },
            },
            callbackUrl,
            endSessionCanRedirect: config.auth.oidc.endSessionCanRedirect,
            isAutoRefreshEnabled: false,
        })
        : undefined,
        [callbackUrl, config.auth]);

    const withOIDCClient = useWithOIDCClient(() => oidcClient || null);

    const apis = useMemo(() => {
        return createConfiguredApiCalls(config.servers || {}, config.auth ?? {}, oidcClient, props.onMaintenance);
    }, [config.servers, config.auth, oidcClient, props.onMaintenance]);

    const withRevokeToken = useWithRevokeToken(apis.revokeToken || null);

    const projectRepository = apis.roleApiCall
        ? new ApiProjectRepository(apis.roleApiCall)
        : new DummyProjectRepository();

    const environmentRepository = apis.fmsApiCall
        ? new ApiEnvironmentRepository(apis.fmsApiCall)
        : new DummyEnvironmentRepository();

    const vehicleRepositoryRaw = apis.vehicleApiCall && apis.mediaDeviceApiCall && apis.controlApiCall && apis.controlStream
        ? new ApiVehicleRepository(apis.vehicleApiCall, apis.mediaDeviceApiCall, apis.controlApiCall, apis.controlStream)
        : new DummyVehicleRepository();
    const vehicleRepository = new CachedVehicleRepository(vehicleRepositoryRaw);

    const mediaDeviceRepository = apis.mediaDeviceApiCall && apis.mediaDeviceStream
        ? new ApiMediaDeviceRepository(apis.mediaDeviceApiCall, apis.mediaDeviceStream)
        : new DummyMediaDeviceRepository();

    const channelResolver = apis.mediaDeviceApiCall
        ? new ApiChannelResolver(apis.mediaDeviceApiCall, mediaDeviceRepository)
        : new DummyChannelResolver();

    const cameraLayoutRepository = new LocalStorageCameraLayoutRepository();

    const authTokenRepository = apis.authApiCall
        ? new AuthTokenRepositoryImpl(apis.authTokenKeeper, apis.authApiCall)
        : new DummyAuthTokenRepository();

    const vehicleConfigurationRepository = new VehicleConfigurationRepository(localStorage);

    const vehicleTelemetryRepositoryFactory = apis.fmsStream && apis.authTokenKeeper
        ? new VehicleTelemetryRepositoryFactory(new BasicFMSWebsocketAPIFactory(apis.fmsStream, apis.authTokenKeeper))
        : new DummyTelemetryRepositoryFactory();

    const dependencies = {
        projectRepository,
        environmentRepository,
        vehicleRepository,
        mediaDeviceRepository,
        channelResolver,
        cameraLayoutRepository,
        authTokenRepository,
        vehicleConfigurationRepository,
        vehicleTelemetryRepositoryFactory,
    };

    const withDeps = buildDependencies(dependencies);

    return withRevokeToken(withOIDCClient(withDeps(
        <React.Fragment>{props.children}</React.Fragment>
    )));
};

export function withDependencies(config: ConfigParams, root: JSX.Element) {
    const onMaintenance = () => {
        console.error('maintenance mode');
        if (config.auth?.oidc.issuer) {
            location.href = config.auth?.oidc.issuer;
        }
    };
    return (
        <DependenciesContainer config={config} onMaintenance={onMaintenance}>{root}</DependenciesContainer>
    );
}
